import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft,
  Download,
  Save,
  X,
  Menu,
  X as CloseIcon,
  Type,
  Shapes,
  Settings,
  MessageCircle,
  LogOut,
} from "lucide-react";
import { Link } from "react-router-dom";
import { KenteCanvas } from "@/components/KenteCanvas";
import { PatternSelector } from "@/components/PatternSelector";
import { SymbolSelector } from "@/components/SymbolSelector";
import { BottomSheet } from "@/components/BottomSheet";
import { ClassDesigner } from "@/components/ClassDesigner";
import ExportDialog from "@/components/ExportDialog";
import { KenteAI } from "@/components/KenteAI";
import { FloatingChat } from "@/components/FloatingChat";
import TemplateCustomizer from "@/components/TemplateCustomizer";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import InCanvasFeedback from "@/components/InCanvasFeedback";
import DesignerLoginPopup from "@/components/DesignerLoginPopup";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Canvas as FabricCanvas } from "fabric";

interface Symbol {
  id: string;
  name: string;
  meaning: string;
  image: string;
  category: "adinkra" | "other";
}

interface TextBox {
  id: string;
  text: string;
  color: string;
  left: number;
  top: number;
  fontSize: number;
}

interface TemplateSymbol {
  position: "left" | "right";
  symbol: Symbol;
  useInscriptionPosition?: boolean;
}

interface TemplateInscription {
  position: "left" | "right";
  text: string;
  useLogoPosition?: boolean;
}

const Designer = () => {
  const [searchParams] = useSearchParams();
  const [selectedPattern, setSelectedPattern] = useState("sash-1");
  const [sashText, setSashText] = useState("");
  const [textColor, setTextColor] = useState("#FFD700"); // Default to yellow
  const [selectedSymbols, setSelectedSymbols] = useState<Symbol[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [textBoxes, setTextBoxes] = useState<TextBox[]>([]);
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);
  const [bottomSheetType, setBottomSheetType] = useState<
    | "symbols"
    | "text"
    | "customize"
    | "left-logo"
    | "right-logo"
    | "left-inscription"
    | "right-inscription"
    | "kente-ai"
    | null
  >(null);
  const [templateSymbols, setTemplateSymbols] = useState<TemplateSymbol[]>([]);
  const [templateInscriptions, setTemplateInscriptions] = useState<
    TemplateInscription[]
  >([]);
  const [showPositioningGuides, setShowPositioningGuides] = useState(false);

  // Feedback states
  const [showExitIntentPopup, setShowExitIntentPopup] = useState(false);
  const [showInCanvasFeedback, setShowInCanvasFeedback] = useState(false);
  const [hasShownExitIntent, setHasShownExitIntent] = useState(false);

  // Authentication states
  const [user, setUser] = useState<any>(null); // eslint-disable-line @typescript-eslint/no-explicit-any
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // Admin settings state
  const [guidesToggleDisabled, setGuidesToggleDisabled] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Save design states
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [designName, setDesignName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Canvas ref for export functionality
  const canvasRef = useRef<FabricCanvas | null>(null);

  // Debug: Log canvas ref changes
  useEffect(() => {
    console.log("Canvas ref updated:", canvasRef.current);
  }, []);

  const checkAdminRole = useCallback(async () => {
    try {
      const { data: adminCheck, error: adminError } = await supabase
        .rpc("is_admin" as any)
        .single();

      if (adminError) {
        console.error("Error checking admin role:", adminError);
        setIsAdmin(false);
        return;
      }

      setIsAdmin(adminCheck === true);
    } catch (error) {
      console.error("Error checking admin role:", error);
      setIsAdmin(false);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const {
        data: { user: currentUser },
      } = await supabase.auth.getUser();
      if (currentUser) {
        setUser(currentUser);
        setIsAuthenticated(true);
        // Check admin role for already authenticated user
        checkAdminRole();
      }
    } catch (error) {
      console.error("Error checking auth:", error);
    } finally {
      setAuthLoading(false);
    }
  }, [checkAdminRole]);

  // Check authentication on component mount
  useEffect(() => {
    checkAuth();
    loadAdminSetting();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && session?.user) {
        setUser(session.user);
        setIsAuthenticated(true);
        setShowLoginPopup(false);
        toast.success("Welcome! You can now start designing.");
        // Check admin role when user signs in
        checkAdminRole();
      } else if (event === "SIGNED_OUT") {
        setUser(null);
        setIsAuthenticated(false);
        setIsAdmin(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [checkAuth, checkAdminRole]);

  const handleAuthRequired = () => {
    setShowLoginPopup(true);
    toast.info("Please login to start designing");
  };

  const handleLoginSuccess = () => {
    setShowLoginPopup(false);
    // User state will be updated by the auth listener
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      toast.success("Logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Failed to logout. Please try again.");
    }
  };

  const loadAdminSetting = async () => {
    try {
      const { data, error } = await supabase.rpc("get_admin_setting" as any, {
        // eslint-disable-line @typescript-eslint/no-explicit-any
        setting_key_param: "designer_guides_hidden",
      });

      if (error) {
        console.error("Error loading admin setting:", error);
        return;
      }

      // If guides are hidden by admin, disable the toggle
      setGuidesToggleDisabled(data === true || data === "true");
    } catch (error) {
      console.error("Error loading admin setting:", error);
    }
  };

  const handleSaveDesign = async () => {
    if (!designName.trim()) {
      toast.error("Please enter a design name");
      return;
    }

    if (!canvasRef.current) {
      toast.error("Canvas not available for saving");
      return;
    }

    if (!isAuthenticated) {
      handleAuthRequired();
      return;
    }

    setIsSaving(true);
    try {
      // Get current user
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        toast.error("Please login to save designs");
        return;
      }

      // Get canvas data
      const canvasData = canvasRef.current.toJSON();

      // Prepare design data
      const designData = {
        canvas: canvasData,
        pattern: selectedPattern,
        symbols: selectedSymbols,
        textBoxes: textBoxes,
        sashText: sashText,
        textColor: textColor,
        templateSymbols: templateSymbols,
        templateInscriptions: templateInscriptions,
      };

      // Save to database
      const { error } = await supabase.from("customer_designs").insert({
        customer_id: user.id,
        design_name: designName.trim(),
        design_data: designData as any, // eslint-disable-line @typescript-eslint/no-explicit-any
      });

      if (error) throw error;

      toast.success("Design saved successfully!");
      setShowSaveDialog(false);
      setDesignName("");
    } catch (error: any) {
      // eslint-disable-line @typescript-eslint/no-explicit-any
      console.error("Error saving design:", error);
      toast.error("Failed to save design. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // Exit intent detection
  useEffect(() => {
    const handleMouseLeave = (e: MouseEvent) => {
      // Only trigger if mouse is leaving from the top of the page
      if (e.clientY <= 0 && !hasShownExitIntent) {
        setShowExitIntentPopup(true);
        setHasShownExitIntent(true);
      }
    };

    const handleBeforeUnload = () => {
      if (!hasShownExitIntent) {
        setShowExitIntentPopup(true);
        setHasShownExitIntent(true);
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasShownExitIntent]);

  // Check for class link parameters
  const classParam = searchParams.get("class");
  const templateParam = searchParams.get("template");
  const sellerParam = searchParams.get("seller");

  // Mock class data - in real app, this would come from API
  const classData = {
    CS2025: {
      name: "Computer Science Class of 2025",
      templateId: "sash-1",
      templateName: "Traditional Gold",
    },
    BA2025: {
      name: "Business Administration 2025",
      templateId: "sash-3",
      templateName: "Graduation Special",
    },
    ENG2025: {
      name: "Engineering Class of 2025",
      templateId: "sash-2",
      templateName: "Royal Blue",
    },
  };

  // If this is a class link, show the class designer
  if (
    classParam &&
    templateParam &&
    classData[classParam as keyof typeof classData]
  ) {
    const classInfo = classData[classParam as keyof typeof classData];
    return (
      <ClassDesigner
        className={classInfo.name}
        templateId={templateParam}
        templateName={classInfo.templateName}
      />
    );
  }

  const handleAddSymbol = (symbol: Symbol) => {
    if (!isAuthenticated) {
      handleAuthRequired();
      return;
    }
    setSelectedSymbols((prev) => [...prev, symbol]);
  };

  const handleRemoveSymbol = (symbolId: string) => {
    setSelectedSymbols((prev) =>
      prev.filter((symbol) => symbol.id !== symbolId)
    );
  };

  const handleAddTemplateSymbol = (
    position: "left" | "right",
    symbol: Symbol,
    useInscriptionPosition: boolean = false
  ) => {
    if (!isAuthenticated) {
      handleAuthRequired();
      return;
    }
    // Remove any existing text in the same position
    if (useInscriptionPosition) {
      // Remove text from inscription position
      setTemplateInscriptions((prev) =>
        prev.filter(
          (inscription) =>
            inscription.position !== position ||
            inscription.useLogoPosition !== false
        )
      );
    } else {
      // Remove text from logo position
      setTemplateInscriptions((prev) =>
        prev.filter(
          (inscription) =>
            inscription.position !== position ||
            inscription.useLogoPosition !== true
        )
      );
    }

    setTemplateSymbols((prev) => {
      const filtered = prev.filter(
        (templateSymbol) =>
          templateSymbol.position !== position ||
          templateSymbol.useInscriptionPosition !== useInscriptionPosition
      );
      return [...filtered, { position, symbol, useInscriptionPosition }];
    });
  };

  const handleRemoveTemplateSymbol = (
    position: "left" | "right",
    useInscriptionPosition: boolean = false
  ) => {
    setTemplateSymbols((prev) =>
      prev.filter(
        (templateSymbol) =>
          templateSymbol.position !== position ||
          templateSymbol.useInscriptionPosition !== useInscriptionPosition
      )
    );
  };

  const handleAddTemplateInscription = (
    position: "left" | "right",
    text: string,
    useLogoPosition: boolean = false
  ) => {
    if (!isAuthenticated) {
      handleAuthRequired();
      return;
    }
    // Remove any existing symbol in the same position
    if (!useLogoPosition) {
      // Remove symbol from inscription position
      setTemplateSymbols((prev) =>
        prev.filter(
          (templateSymbol) =>
            templateSymbol.position !== position ||
            templateSymbol.useInscriptionPosition !== true
        )
      );
    } else {
      // Remove symbol from logo position
      setTemplateSymbols((prev) =>
        prev.filter(
          (templateSymbol) =>
            templateSymbol.position !== position ||
            templateSymbol.useInscriptionPosition !== false
        )
      );
    }

    setTemplateInscriptions((prev) => {
      const filtered = prev.filter(
        (inscription) =>
          inscription.position !== position ||
          inscription.useLogoPosition !== useLogoPosition
      );
      return [...filtered, { position, text, useLogoPosition }];
    });
  };

  const handleRemoveTemplateInscription = (
    position: "left" | "right",
    useLogoPosition: boolean = false
  ) => {
    setTemplateInscriptions((prev) =>
      prev.filter(
        (inscription) =>
          inscription.position !== position ||
          inscription.useLogoPosition !== useLogoPosition
      )
    );
  };

  const handleExportPositions = (templateId: string) => {
    // Get the canvas element
    const canvasElement = document.querySelector("canvas");
    if (!canvasElement) return;

    // Get the fabric canvas instance
    const fabricCanvas = canvasRef.current;
    if (!fabricCanvas) return;

    // Get all positioning guides
    const objects = fabricCanvas.getObjects();
    const guides = objects.filter(
      (obj: any) => obj.isPositioningGuide && obj.guideType // eslint-disable-line @typescript-eslint/no-explicit-any
    );

    // Extract position data
    const positions: Record<string, any> = {
      // eslint-disable-line @typescript-eslint/no-explicit-any
      left: {},
      right: {},
    };

    guides.forEach((guide: any) => {
      // eslint-disable-line @typescript-eslint/no-explicit-any
      const x = Math.round(guide.left || 0);
      const y = Math.round(guide.top || 0);
      const w = Math.round((guide.width || 0) * (guide.scaleX || 1));
      const h = Math.round((guide.height || 0) * (guide.scaleY || 1));

      const positionData = {
        x,
        y,
        width: w,
        height: h,
        fontSize: guide.guideType === "inscription" ? 24 : undefined,
      };

      if (guide.guideSide === "left") {
        positions.left[guide.guideType] = positionData;
      } else {
        positions.right[guide.guideType] = positionData;
      }
    });

    // Create the code to copy
    const codeToCopy = `"${templateId}": ${JSON.stringify(positions, null, 2)
      .replace(/\n/g, "\n  ")
      .replace(/^/, "  ")},`;

    // Log the positions for easy copying
    console.log(`\n=== ${templateId.toUpperCase()} POSITIONS ===`);
    console.log(JSON.stringify(positions, null, 2));
    console.log(
      `\nCopy this to update ${templateId} in templatePositions.ts:\n`
    );
    console.log(codeToCopy);

    // Copy to clipboard
    navigator.clipboard
      .writeText(codeToCopy)
      .then(() => {
        // Show success message with copied values
        alert(
          `✅ Positions copied to clipboard!\n\nTemplate: ${templateId}\n\nLeft Logo: X:${
            positions.left.logo?.x || "N/A"
          } Y:${positions.left.logo?.y || "N/A"} W:${
            positions.left.logo?.width || "N/A"
          } H:${positions.left.logo?.height || "N/A"}\nRight Logo: X:${
            positions.right.logo?.x || "N/A"
          } Y:${positions.right.logo?.y || "N/A"} W:${
            positions.right.logo?.width || "N/A"
          } H:${positions.right.logo?.height || "N/A"}\nLeft Text: X:${
            positions.left.inscription?.x || "N/A"
          } Y:${positions.left.inscription?.y || "N/A"} W:${
            positions.left.inscription?.width || "N/A"
          } H:${positions.left.inscription?.height || "N/A"}\nRight Text: X:${
            positions.right.inscription?.x || "N/A"
          } Y:${positions.right.inscription?.y || "N/A"} W:${
            positions.right.inscription?.width || "N/A"
          } H:${
            positions.right.inscription?.height || "N/A"
          }\n\n📋 Code copied to clipboard - paste it into templatePositions.ts`
        );
      })
      .catch((err) => {
        // Fallback if clipboard API fails
        console.error("Failed to copy to clipboard:", err);
        alert(
          `Positions exported for ${templateId}!\n\nCheck the browser console for the exact code to copy.\n\nTemplate: ${templateId}\nLeft Logo: X:${
            positions.left.logo?.x || "N/A"
          } Y:${positions.left.logo?.y || "N/A"} W:${
            positions.left.logo?.width || "N/A"
          } H:${positions.left.logo?.height || "N/A"}\nRight Logo: X:${
            positions.right.logo?.x || "N/A"
          } Y:${positions.right.logo?.y || "N/A"} W:${
            positions.right.logo?.width || "N/A"
          } H:${positions.right.logo?.height || "N/A"}\nLeft Text: X:${
            positions.left.inscription?.x || "N/A"
          } Y:${positions.left.inscription?.y || "N/A"} W:${
            positions.left.inscription?.width || "N/A"
          } H:${positions.left.inscription?.height || "N/A"}\nRight Text: X:${
            positions.right.inscription?.x || "N/A"
          } Y:${positions.right.inscription?.y || "N/A"} W:${
            positions.right.inscription?.width || "N/A"
          } H:${positions.right.inscription?.height || "N/A"}`
        );
      });
  };

  const handleAddText = () => {
    if (!isAuthenticated) {
      handleAuthRequired();
      return;
    }
    const newTextBox: TextBox = {
      id: `text-${Date.now()}`,
      text: sashText || "New Text",
      color: textColor,
      left: 200,
      top: 300,
      fontSize: 32,
    };
    setTextBoxes((prev) => [...prev, newTextBox]);
    setSashText(""); // Clear the input after adding

    // Auto-close bottom sheet if it's the text sheet
    if (bottomSheetType === "text") {
      closeBottomSheet();
    }
  };

  const handleRemoveText = (textId: string) => {
    setTextBoxes((prev) => prev.filter((textBox) => textBox.id !== textId));
  };

  const openBottomSheet = (
    type:
      | "symbols"
      | "text"
      | "customize"
      | "left-logo"
      | "right-logo"
      | "left-inscription"
      | "right-inscription"
      | "kente-ai"
  ) => {
    // Check authentication for design actions
    if (type !== "kente-ai" && !isAuthenticated) {
      handleAuthRequired();
      return;
    }
    setBottomSheetType(type);
    setBottomSheetOpen(true);
  };

  const closeBottomSheet = () => {
    setBottomSheetOpen(false);
    setBottomSheetType(null);
  };

  // Feedback handlers
  const handleExitIntentFeedback = async (feedback: {
    reason: string;
    message: string;
  }) => {
    console.log("Exit intent feedback:", feedback);
    // Here you would send to your backend
    // await fetch('/api/exit-feedback', { ... });
  };

  const handleInCanvasFeedback = async (feedback: {
    type: string;
    message: string;
    context: string;
  }) => {
    console.log("In-canvas feedback:", feedback);
    // Here you would send to your backend
    // await fetch('/api/canvas-feedback', { ... });
  };

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading designer...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/">
              <Button variant="ghost" size="sm" className="text-xs sm:text-sm">
                <ArrowLeft className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Back to Home</span>
                <span className="sm:hidden">Back</span>
              </Button>
            </Link>
            <h1 className="text-sm sm:text-xl font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
              <span className="hidden sm:inline">Kente Designer</span>
              <span className="sm:hidden">Designer</span>
            </h1>
            <div className="flex items-center justify-end">
              <div className="flex gap-1 sm:gap-2 items-center">
                {isAuthenticated && user && (
                  <div className="flex items-center gap-2">
                    <div className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span>Welcome, {user.email?.split("@")[0]}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleLogout}
                      className="text-xs sm:text-sm h-8 px-2 sm:px-3"
                    >
                      <LogOut className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                      <span className="hidden sm:inline">Logout</span>
                    </Button>
                  </div>
                )}
                <ExportDialog canvasRef={canvasRef} />
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-4 sm:py-8">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-8 h-[calc(100vh-120px)] lg:h-[calc(100vh-120px)]">
          {/* Mobile Sidebar Overlay */}
          {sidebarOpen && (
            <div
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )}

          {/* Sidebar Controls */}
          <aside
            className={`
            fixed lg:relative top-0 h-full lg:h-auto w-80 lg:w-[350px]
            bg-card border-r lg:border-r-0 border-border z-50 lg:z-auto
            transition-all duration-300 ease-in-out
            space-y-4 lg:space-y-6 p-4 lg:p-0 overflow-y-auto lg:overflow-y-auto
            flex-shrink-0
            ${sidebarOpen ? "left-0" : "-left-80 lg:left-0"}
          `}
          >
            {/* Mobile Close Button */}
            <div className="flex justify-between items-center lg:hidden mb-4">
              <h2 className="text-lg font-semibold">Design Options</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarOpen(false)}
                className="p-2"
              >
                <CloseIcon className="h-4 w-4" />
              </Button>
            </div>
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">Sash Template</h2>
              <PatternSelector
                selectedPattern={selectedPattern}
                onSelectPattern={setSelectedPattern}
              />
            </Card>

            <TemplateCustomizer
              selectedPattern={selectedPattern}
              onAddSymbol={handleAddTemplateSymbol}
              onAddInscription={handleAddTemplateInscription}
              onRemoveSymbol={handleRemoveTemplateSymbol}
              onRemoveInscription={handleRemoveTemplateInscription}
              showPositioningGuides={showPositioningGuides}
              onTogglePositioningGuides={
                guidesToggleDisabled ? undefined : setShowPositioningGuides
              }
              onExportPositions={handleExportPositions}
              isAdmin={isAdmin}
            />

            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">Text Customization</h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="sash-text">Add Text</Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      id="sash-text"
                      placeholder="e.g., Class of 2025"
                      value={sashText}
                      onChange={(e) => setSashText(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      onClick={handleAddText}
                      disabled={!sashText.trim()}
                      size="sm"
                    >
                      Add
                    </Button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="font-size">Font Size</Label>
                  <Input
                    id="font-size"
                    type="range"
                    min="20"
                    max="80"
                    defaultValue="40"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="text-color">Text Color</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Input
                      id="text-color"
                      type="color"
                      value={textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                      className="w-12 h-10 p-1 border rounded cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                      placeholder="#FFD700"
                      className="flex-1 text-sm"
                    />
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <SymbolSelector onAddSymbol={handleAddSymbol} />
            </Card>

            {textBoxes.length > 0 && (
              <Card className="p-6">
                <h2 className="text-lg font-semibold mb-4">Text Elements</h2>
                <div className="space-y-2">
                  {textBoxes.map((textBox) => (
                    <div
                      key={textBox.id}
                      className="flex items-center justify-between p-2 bg-muted/50 rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-4 h-4 rounded border"
                          style={
                            {
                              backgroundColor: textBox.color,
                            } as React.CSSProperties
                          }
                        />
                        <span className="text-sm font-medium truncate">
                          {textBox.text}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveText(textBox.id)}
                        className="h-6 w-6 p-0"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {selectedSymbols.length > 0 && (
              <Card className="p-6">
                <h2 className="text-lg font-semibold mb-4">Selected Symbols</h2>
                <div className="space-y-2">
                  {selectedSymbols.map((symbol) => (
                    <div
                      key={symbol.id}
                      className="flex items-center justify-between p-2 bg-muted/50 rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <img
                          src={symbol.image}
                          alt={symbol.name}
                          className="w-6 h-6"
                        />
                        <span className="text-sm font-medium">
                          {symbol.name}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveSymbol(symbol.id)}
                        className="h-6 w-6 p-0"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <h3 className="font-semibold mb-2">Save Your Design</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Save your design to access it later
              </p>
              <Button
                variant="outline"
                className="w-full"
                size="lg"
                onClick={() => setShowSaveDialog(true)}
              >
                <Save className="mr-2 h-4 w-4" />
                Save Design
              </Button>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20">
              <h3 className="font-semibold mb-2">Ready to Order?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Proceed to checkout
              </p>
              <Button variant="hero" className="w-full" size="lg">
                Add to Cart
              </Button>
            </Card>
          </aside>

          {/* Canvas Area */}
          <main className="order-first lg:order-none flex-1 flex flex-col min-h-0">
            <Card className="p-4 sm:p-6 lg:p-8 bg-card flex-1 flex flex-col min-h-0">
              <div className="mb-4 lg:hidden flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setSidebarOpen(true)}
                >
                  <Menu className="mr-2 h-4 w-4" />
                  Design Options
                </Button>
                <Button
                  onClick={() => openBottomSheet("kente-ai")}
                  className="flex items-center gap-2"
                >
                  <MessageCircle className="h-4 w-4" />
                  Kente AI
                </Button>
              </div>
              <div className="flex-1 min-h-0 relative">
                <KenteCanvas
                  selectedPattern={selectedPattern}
                  textBoxes={textBoxes}
                  symbols={selectedSymbols}
                  templateSymbols={templateSymbols}
                  templateInscriptions={templateInscriptions}
                  onRemoveTextBox={handleRemoveText}
                  onRemoveSymbol={handleRemoveSymbol}
                  onRemoveTemplateSymbol={handleRemoveTemplateSymbol}
                  onRemoveTemplateInscription={handleRemoveTemplateInscription}
                  canvasRef={canvasRef}
                  showPositioningGuides={showPositioningGuides}
                />

                {/* In-Canvas Feedback Button */}
                <Button
                  variant="outline"
                  size="sm"
                  className="absolute top-2 right-2 sm:top-4 sm:right-4 bg-background/90 backdrop-blur-sm border-border/50 hover:bg-background/95 z-10 text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-2"
                  onClick={() => setShowInCanvasFeedback(true)}
                >
                  <MessageCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Feedback</span>
                  <span className="sm:hidden">💬</span>
                </Button>
              </div>
            </Card>

            {/* Mobile Bottom Buttons */}
            <div className="lg:hidden fixed bottom-4 left-4 right-4 z-30">
              <Button
                onClick={() => openBottomSheet("customize")}
                className="w-full h-12 bg-primary text-primary-foreground"
                size="lg"
              >
                <Settings className="mr-2 h-5 w-5" />
                Customize
              </Button>
            </div>
          </main>
        </div>
      </div>

      {/* Mobile Bottom Sheets */}
      <BottomSheet
        isOpen={bottomSheetOpen && bottomSheetType === "customize"}
        onClose={closeBottomSheet}
        title="Customize Your Sash"
      >
        <div className="p-6 space-y-6">
          {/* Customization Options */}
          <div className="grid grid-cols-2 gap-4">
            {/* Left Logo */}
            <Button
              onClick={() => openBottomSheet("left-logo")}
              className="h-20 flex flex-col items-center justify-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-700 border-2 border-blue-200"
            >
              <Shapes className="h-6 w-6" />
              <span className="text-sm font-medium">Left Logo</span>
            </Button>

            {/* Right Logo */}
            <Button
              onClick={() => openBottomSheet("right-logo")}
              className="h-20 flex flex-col items-center justify-center gap-2 bg-green-50 hover:bg-green-100 text-green-700 border-2 border-green-200"
            >
              <Shapes className="h-6 w-6" />
              <span className="text-sm font-medium">Right Logo</span>
            </Button>

            {/* Left Inscription */}
            <Button
              onClick={() => openBottomSheet("left-inscription")}
              className="h-20 flex flex-col items-center justify-center gap-2 bg-purple-50 hover:bg-purple-100 text-purple-700 border-2 border-purple-200"
            >
              <Type className="h-6 w-6" />
              <span className="text-sm font-medium">Left Text</span>
            </Button>

            {/* Right Inscription */}
            <Button
              onClick={() => openBottomSheet("right-inscription")}
              className="h-20 flex flex-col items-center justify-center gap-2 bg-orange-50 hover:bg-orange-100 text-orange-700 border-2 border-orange-200"
            >
              <Type className="h-6 w-6" />
              <span className="text-sm font-medium">Right Text</span>
            </Button>
          </div>
        </div>
      </BottomSheet>

      {/* Left Logo Bottom Sheet */}
      <BottomSheet
        isOpen={bottomSheetOpen && bottomSheetType === "left-logo"}
        onClose={closeBottomSheet}
        title="Left Logo"
      >
        <div className="p-6 space-y-4">
          {/* Option to use text instead of symbol */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <Label className="text-sm font-medium mb-2 block">
              Or use text instead of a symbol:
            </Label>
            <div className="flex gap-2">
              <Input
                placeholder="Enter text for left logo position"
                className="flex-1"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const target = e.target as HTMLInputElement;
                    if (target.value.trim()) {
                      handleAddTemplateInscription(
                        "left",
                        target.value.trim(),
                        true
                      );
                      target.value = "";
                      closeBottomSheet();
                    }
                  }
                }}
              />
              <Button
                size="sm"
                onClick={() => {
                  const input = document.querySelector(
                    'input[placeholder*="left logo position"]'
                  ) as HTMLInputElement;
                  if (input?.value.trim()) {
                    handleAddTemplateInscription(
                      "left",
                      input.value.trim(),
                      true
                    );
                    input.value = "";
                    closeBottomSheet();
                  }
                }}
              >
                Add Text
              </Button>
            </div>
          </div>

          {/* Symbol selector */}
          <div>
            <Label className="text-sm font-medium mb-2 block">
              Select a symbol:
            </Label>
            <SymbolSelector
              onAddSymbol={(symbol) => {
                handleAddTemplateSymbol("left", symbol);
                closeBottomSheet();
              }}
              onClose={closeBottomSheet}
            />
          </div>
        </div>
      </BottomSheet>

      {/* Right Logo Bottom Sheet */}
      <BottomSheet
        isOpen={bottomSheetOpen && bottomSheetType === "right-logo"}
        onClose={closeBottomSheet}
        title="Right Logo"
      >
        <div className="p-6 space-y-4">
          {/* Option to use text instead of symbol */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <Label className="text-sm font-medium mb-2 block">
              Or use text instead of a symbol:
            </Label>
            <div className="flex gap-2">
              <Input
                placeholder="Enter text for right logo position"
                className="flex-1"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const target = e.target as HTMLInputElement;
                    if (target.value.trim()) {
                      handleAddTemplateInscription(
                        "right",
                        target.value.trim(),
                        true
                      );
                      target.value = "";
                      closeBottomSheet();
                    }
                  }
                }}
              />
              <Button
                size="sm"
                onClick={() => {
                  const input = document.querySelector(
                    'input[placeholder*="right logo position"]'
                  ) as HTMLInputElement;
                  if (input?.value.trim()) {
                    handleAddTemplateInscription(
                      "right",
                      input.value.trim(),
                      true
                    );
                    input.value = "";
                    closeBottomSheet();
                  }
                }}
              >
                Add Text
              </Button>
            </div>
          </div>

          {/* Symbol selector */}
          <div>
            <Label className="text-sm font-medium mb-2 block">
              Select a symbol:
            </Label>
            <SymbolSelector
              onAddSymbol={(symbol) => {
                handleAddTemplateSymbol("right", symbol);
                closeBottomSheet();
              }}
              onClose={closeBottomSheet}
            />
          </div>
        </div>
      </BottomSheet>

      {/* Left Inscription Bottom Sheet */}
      <BottomSheet
        isOpen={bottomSheetOpen && bottomSheetType === "left-inscription"}
        onClose={closeBottomSheet}
        title="Left Inscription"
      >
        <div className="p-6 space-y-4">
          {/* Text input option */}
          <div>
            <Label htmlFor="left-inscription-text">Add Text</Label>
            <div className="flex gap-2 mt-2">
              <Input
                id="left-inscription-text"
                placeholder="Enter left inscription text"
                className="flex-1"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const target = e.target as HTMLInputElement;
                    if (target.value.trim()) {
                      handleAddTemplateInscription("left", target.value.trim());
                      target.value = "";
                      closeBottomSheet();
                    }
                  }
                }}
              />
              <Button
                onClick={() => {
                  const input = document.getElementById(
                    "left-inscription-text"
                  ) as HTMLInputElement;
                  if (input.value.trim()) {
                    handleAddTemplateInscription("left", input.value.trim());
                    input.value = "";
                    closeBottomSheet();
                  }
                }}
                size="sm"
              >
                Add Text
              </Button>
            </div>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-2">
            <div className="flex-1 h-px bg-gray-200"></div>
            <span className="text-sm text-gray-500">OR</span>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>

          {/* Symbol selector option */}
          <div>
            <Label className="text-sm font-medium mb-2 block">
              Use a symbol instead:
            </Label>
            <SymbolSelector
              onAddSymbol={(symbol) => {
                handleAddTemplateSymbol("left", symbol, true);
                closeBottomSheet();
              }}
              onClose={closeBottomSheet}
            />
          </div>
        </div>
      </BottomSheet>

      {/* Right Inscription Bottom Sheet */}
      <BottomSheet
        isOpen={bottomSheetOpen && bottomSheetType === "right-inscription"}
        onClose={closeBottomSheet}
        title="Right Inscription"
      >
        <div className="p-6 space-y-4">
          {/* Text input option */}
          <div>
            <Label htmlFor="right-inscription-text">Add Text</Label>
            <div className="flex gap-2 mt-2">
              <Input
                id="right-inscription-text"
                placeholder="Enter right inscription text"
                className="flex-1"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const target = e.target as HTMLInputElement;
                    if (target.value.trim()) {
                      handleAddTemplateInscription(
                        "right",
                        target.value.trim()
                      );
                      target.value = "";
                      closeBottomSheet();
                    }
                  }
                }}
              />
              <Button
                onClick={() => {
                  const input = document.getElementById(
                    "right-inscription-text"
                  ) as HTMLInputElement;
                  if (input.value.trim()) {
                    handleAddTemplateInscription("right", input.value.trim());
                    input.value = "";
                    closeBottomSheet();
                  }
                }}
                size="sm"
              >
                Add Text
              </Button>
            </div>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-2">
            <div className="flex-1 h-px bg-gray-200"></div>
            <span className="text-sm text-gray-500">OR</span>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>

          {/* Symbol selector option */}
          <div>
            <Label className="text-sm font-medium mb-2 block">
              Use a symbol instead:
            </Label>
            <SymbolSelector
              onAddSymbol={(symbol) => {
                handleAddTemplateSymbol("right", symbol, true);
                closeBottomSheet();
              }}
              onClose={closeBottomSheet}
            />
          </div>
        </div>
      </BottomSheet>

      {/* Legacy Text Bottom Sheet (for backward compatibility) */}
      <BottomSheet
        isOpen={bottomSheetOpen && bottomSheetType === "text"}
        onClose={closeBottomSheet}
        title="Text Customization"
      >
        <div className="p-6 space-y-6">
          {/* Add Text Section */}
          <div>
            <Label htmlFor="mobile-sash-text">Add Text</Label>
            <div className="flex gap-2 mt-2">
              <Input
                id="mobile-sash-text"
                placeholder="e.g., Class of 2025"
                value={sashText}
                onChange={(e) => setSashText(e.target.value)}
                className="flex-1"
              />
              <Button
                onClick={handleAddText}
                disabled={!sashText.trim()}
                size="sm"
              >
                Add
              </Button>
            </div>
          </div>

          {/* Text Color */}
          <div>
            <Label htmlFor="mobile-text-color">Text Color</Label>
            <div className="flex items-center gap-2 mt-2">
              <Input
                id="mobile-text-color"
                type="color"
                value={textColor}
                onChange={(e) => setTextColor(e.target.value)}
                className="w-12 h-10 p-1 border rounded cursor-pointer"
              />
              <Input
                type="text"
                value={textColor}
                onChange={(e) => setTextColor(e.target.value)}
                placeholder="#FFD700"
                className="flex-1 text-sm"
              />
            </div>
          </div>

          {/* Text Elements List */}
          {textBoxes.length > 0 && (
            <div>
              <Label>Text Elements</Label>
              <div className="space-y-2 mt-2">
                {textBoxes.map((textBox) => (
                  <div
                    key={textBox.id}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-4 h-4 rounded border"
                        style={
                          {
                            backgroundColor: textBox.color,
                          } as React.CSSProperties
                        }
                      />
                      <span className="text-sm font-medium truncate">
                        {textBox.text}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveText(textBox.id)}
                      className="h-8 w-8 p-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </BottomSheet>

      {/* Kente AI Bottom Sheet */}
      <BottomSheet
        isOpen={bottomSheetOpen && bottomSheetType === "kente-ai"}
        onClose={closeBottomSheet}
        title="Kente AI"
      >
        <KenteAI onClose={closeBottomSheet} showHeader={false} />
      </BottomSheet>

      {/* Floating Chat for Desktop */}
      <div className="hidden lg:block">
        <FloatingChat />

        {/* Feedback Components */}
        <ExitIntentPopup
          isOpen={showExitIntentPopup}
          onClose={() => setShowExitIntentPopup(false)}
          onFeedbackSubmit={handleExitIntentFeedback}
        />

        <InCanvasFeedback
          isOpen={showInCanvasFeedback}
          onClose={() => setShowInCanvasFeedback(false)}
          onFeedbackSubmit={handleInCanvasFeedback}
        />

        {/* Save Design Dialog */}
        {showSaveDialog && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>Save Design</CardTitle>
                <CardDescription>
                  Give your design a name to save it
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="design-name">Design Name</Label>
                  <Input
                    id="design-name"
                    value={designName}
                    onChange={(e) => setDesignName(e.target.value)}
                    placeholder="My Kente Design"
                    className="mt-2"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowSaveDialog(false);
                      setDesignName("");
                    }}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveDesign}
                    disabled={isSaving || !designName.trim()}
                  >
                    {isSaving ? "Saving..." : "Save Design"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Login Popup */}
        <DesignerLoginPopup
          isOpen={showLoginPopup}
          onClose={() => setShowLoginPopup(false)}
          onSuccess={handleLoginSuccess}
        />
      </div>
    </div>
  );
};

export default Designer;
